import type { GeneratedAgent } from "@/api/mock-data/agent-estate/generate";
import {
  getIntentPolicies,
  getMcpServerCatalog,
  type McpServerDefinition,
  withConnected,
} from "@/api/mock-data/agent-estate/seed";
import { toolDescription } from "@/api/mock-data/agent-estate/toolDescriptions";
import type {
  AgentSecurityStatus,
  EstateAgentMcpService,
} from "@/api/temp/AgentEstateService";
import { PLATFORM_NAMES } from "./governance";
import {
  DEMO_AGENT_PROFILES,
  DEMO_RESOURCE_BINDINGS,
  DEMO_SCENARIOS,
  DEMO_SERVER_RESOURCES,
} from "./scenarios";
import { snapshotAccounts } from "./snapshot";
import { sourceDisplayName } from "./sourcePresentation";

/** Expected access in fixture relationships; never a runtime evaluation. */
export function buildDemoMcpServices(
  agent: GeneratedAgent,
  status: AgentSecurityStatus,
  policies: Array<{ id: string; name: string }>,
  revoked: boolean,
  fallback?: McpServerDefinition,
  connected: McpServerDefinition[] = [],
): EstateAgentMcpService[] {
  const scenario = agent.demoScenario
    ? DEMO_SCENARIOS[agent.demoScenario]
    : undefined;
  const profile = agent.demoKey
    ? Object.values(DEMO_AGENT_PROFILES).find(item => item.key === agent.demoKey)
    : undefined;
  const named = scenario ?? profile;
  const discovered = named
    ? getMcpServerCatalog().filter(server =>
        named.serverIds.includes(server.id),
      )
    : fallback
      ? [fallback]
      : [];
  const servers = withConnected(discovered, connected);
  // The Intent Policy an agent carries, where one names it; otherwise the
  // scenario's recommendation, otherwise whatever it was assigned first.
  const intentIds = new Set(
    getIntentPolicies()
      .filter(policy => !!agent.demoKey && policy.agentKeys.includes(agent.demoKey))
      .map(policy => policy.id),
  );
  return servers.map(server => {
    const bindingKey = DEMO_SERVER_RESOURCES[server.id];
    const binding = bindingKey ? DEMO_RESOURCE_BINDINGS[bindingKey] : undefined;
    const source = snapshotAccounts.find(item => item.id === binding?.sourceId);
    const policy = scenario
      ? policies.find(item => item.id === scenario.recommendation.id)
      : profile
        ? policies.find(item => intentIds.has(item.id))
        : policies[0];
    const covered = status === "SECURE" && !!policy;
    const protectionStatus = revoked
      ? "DENIED"
      : covered
        ? "PROTECTED"
        : status === "NOT_EVALUATED"
          ? "REVIEW"
          : "NEEDS_POLICY";
    const dataAccess =
      binding && source
        ? `${sourceDisplayName(source)} · ${binding.resource}`
        : (server.tools[0]?.dataAccess ?? server.name);
    return {
      id: server.id,
      name: server.name,
      technology: server.technology,
      capabilities: scenario ? [scenario.context] : server.capabilities,
      dataAccess: [dataAccess],
      protectionStatus,
      tools: server.tools.map(tool => {
        const outsideDomain =
          !!named &&
          !!binding &&
          binding.domains.some(
            domain => !named.allowedDomains.includes(domain),
          );
        const denied =
          revoked ||
          outsideDomain ||
          tool.classification === "Destructive" ||
          (!!named && tool.classification !== "Read");
        return {
          id: `${server.id}-${tool.name}`,
          name: tool.name,
          description: toolDescription(tool.name, tool.dataAccess),
          classification: tool.classification,
          risk: tool.classification === "Read" ? "Low" : "High",
          dataAccess,
          dataSourceId: source?.id,
          dataSourceName: source ? sourceDisplayName(source) : undefined,
          dataSourcePlatform: source?.type
            ? (PLATFORM_NAMES[source.type] ?? source.type)
            : undefined,
          dataSourcePlatformId: source?.type,
          dataDomains: binding?.domains,
          access: denied ? "DENIED" : "ALLOWED",
          protectionStatus: revoked ? "DENIED" : protectionStatus,
          policyId: covered ? policy.id : undefined,
          policyName: covered ? policy.name : undefined,
        };
      }),
    };
  });
}
